import { PeriodConfig, TIME_PERIOD_CONFIG, TimePeriod } from "./historicalChart.consants";
import { FinraHolidays, Inference } from "@/app/data/dataProvider";
import { snapToTradingHours } from "@/utils/date.utils";
import { DateTime } from "luxon";

export const getTimeArray = ({
  timePeriod,
  finraHolidays,
  settlementDate,
  customPeriod,
  isDuringTradingHours,
  getPrevEoD,
}: {
  timePeriod: TimePeriod,
  settlementDate: Date
  finraHolidays: FinraHolidays
  customPeriod: [number, number] | undefined
  isDuringTradingHours: (ms: number) => boolean
  getPrevEoD: ((ms: number) => number) | null
}): string[] => {
  // don't generate any timestamps until the FINRA calendar has loaded -- isDuringTradingHours can't
  // distinguish "not loaded" (it accepts everything) from a real trading day, so gate on the calendar
  if (!finraHolidays) {
    return [];
  }

  const isCustomPeriod = timePeriod === TimePeriod.Custom;
  let config = TIME_PERIOD_CONFIG[timePeriod];
  if (timePeriod === TimePeriod.Max) {
    config = calculateCustomConfig(settlementDate);
  } else if (isCustomPeriod && customPeriod) {
    const startDate = new Date(customPeriod[0]);
    const endDate = new Date(customPeriod[1]);
    config = calculateCustomConfig(startDate, endDate)
  } else if (!config) {
    return [];
  }

  // for the intraday periods, anchor the window end to the most recent trading instant: if the market
  // is currently closed, end at the last close (getPrevEoD) so e.g. 1D on a weekend shows the most
  // recent trading session instead of an empty window. No-op for longer periods and for in-hours.
  const nowMs = DateTime.now().toMillis();
  const isIntraday = timePeriod === TimePeriod['1D'] || timePeriod === TimePeriod['3H'];
  const anchorMs = isIntraday && !isDuringTradingHours(nowMs) && getPrevEoD ? getPrevEoD(nowMs) : nowMs;
  // anchor in ET so getInitialTime's startOf('day')/setHour align with the America/New_York zone that
  // isDuringTradingHours filters on -- otherwise a non-ET runtime shifts the sampled wall-clock hour
  // (e.g. local 5 PM) out of ET trading hours and the points get filtered out, yielding empty times
  const anchor = DateTime.fromMillis(anchorMs, { zone: 'America/New_York' });

  const { minus, step, getInitialTime, startDate, endDate, setHour } = config
  const periodEnd = isCustomPeriod
    ?  endDate
      ? DateTime.fromJSDate(endDate, { zone: 'America/New_York' })
      : getInitialTime(anchor)
    : getInitialTime(anchor);
  // step the window in ET (not the runtime's local zone) so loop timestamps stay on the same
  // America/New_York grid the trading-hours filter uses, including across DST offset changes
  let periodStart = DateTime.fromMillis(periodEnd.toMillis(), { zone: 'America/New_York' }).minus(minus)
  // interpret in ET (like the anchor) so setHour aligns with the America/New_York trading-hours filter
  const _startDate = startDate
    ? setHour
      ? setHour(DateTime.fromJSDate(startDate, { zone: 'America/New_York' })).toJSDate()
      : startDate
    : undefined;
  const _endDate = endDate
    ? setHour
      ? setHour(DateTime.fromJSDate(endDate, { zone: 'America/New_York' })).toJSDate()
      : endDate
    : undefined


  // Collect candidate sample instants as epoch ms. Out-of-hours samples are snapped back onto the
  // nearest trading instant (snapToTradingHours) rather than dropped -- otherwise a step that locks
  // every sample to one weekday (e.g. 3Y's weekly step) empties the chart whenever that weekday is a
  // weekend. Snapping can map distinct samples onto one instant, so we dedupe and sort at the end.
  const sampleMs: number[] = [];

  
  // add the start boundary if it's within trading hours -- use _startDate (the hour-adjusted value)
  // so the pushed boundary matches the loop's `date <= _startDate` clamp; otherwise, when setHour is
  // applied, the effective start boundary is skipped entirely
  const startBoundary = _startDate ?? startDate;
  if (startBoundary && isDuringTradingHours(startBoundary.getTime())) {
    sampleMs.push(startBoundary.getTime());
  }

  let j = 0;
  for (let i = periodStart; i <= periodEnd; i = i.plus(step)) {
    const date = new Date(i.toMillis());
    

    
    if (_startDate && date <= _startDate) {
      // for custom interval we have bigger period than requested
      // so we need to skip dates which are less than startDate
      continue;
    }

    if (_endDate && date > _endDate) {
      break;
    }

    // Snap to the nearest trading instant. isDuringTradingHours (America/New_York) is the single
    // ET-aware gate (weekends, FINRA holidays, out-of-hours); snapToTradingHours returns the instant
    // unchanged when it's in hours, an earlier trading-day instant when it can be snapped back, or
    // null for an overnight wall-clock time with nowhere to snap -- which we drop, as before.
    const snapped = snapToTradingHours(date.getTime(), isDuringTradingHours);
    if (snapped === null) {
      continue;
    }
    sampleMs.push(snapped);

    if (++j === 2000) {
      throw new Error('Too many iterations');
    }
  }

  // end the window at "now" when the current moment is within trading hours (non-custom periods only)
  if (timePeriod !== TimePeriod.Custom && periodEnd.toMillis() < nowMs && isDuringTradingHours(nowMs)) {
    sampleMs.push(nowMs);
  }

  // snapping can collide distinct samples onto one instant; the inference mapping and trading-time
  // x-axis both require strictly ascending, unique timestamps, so dedupe and sort here
  return Array.from(new Set(sampleMs)).sort((a, b) => a - b).map(ms => new Date(ms).toISOString());
}


// calculates custom config based on the date
function calculateCustomConfig(startDate: Date, endDate?: Date): PeriodConfig {
  const configs = Object.values(TIME_PERIOD_CONFIG).filter(Boolean).reverse();
  const diff = (endDate || new Date()).getTime() - startDate.getTime();

  for (let i = 0; i < configs.length; i++) {
    const config = configs[i];
    const { getInitialTime, minus } = config!;
    const periodStartDateMs = getInitialTime().minus(minus).toMillis();
    const periodDiff = getInitialTime().toMillis() - periodStartDateMs;

    if (diff > periodDiff) {
      const prevConfig = configs[i - 1];
      if (!prevConfig) {
        throw new Error('No prev config found');
      }
      return {
        ...prevConfig,
        startDate: startDate,
        endDate,
      };
    }
  }

  throw new Error('No config found');

}


export type HistoricalChartStatus = 'loading' | 'no-data' | 'ready';

const lastDateMs = (inferences: Inference[]) =>
  inferences.length ? inferences[inferences.length - 1].date.getTime() : -Infinity;

// Decides what the historical chart should show:
// - 'loading' while the FINRA calendar is still loading (getTimeArray returns [] until then, so an
//   empty window can't yet be distinguished from "not loaded"); until the most recent stable requested
//   point has arrived (right edge settled); and thereafter while <=60% of the window is populated -- so
//   switching to a longer period shows the loader rather than a sparse partial chart from the cache.
// - 'no-data' once the calendar is loaded but the window is empty, or once the recent data is in but
//   there are <2 points (a line needs >= 2, and points draw with radius 0 so a single one is
//   invisible) -- this avoids the single-point hang.
// - 'ready' once the recent point is in and > 60% of the window is populated.
export function getHistoricalChartStatus(
  times: string[],
  bidInferences: Inference[],
  dealerInferences: Inference[],
  offerInferences: Inference[],
  finraHolidays: FinraHolidays,
): HistoricalChartStatus {
  if (!finraHolidays) {
    // calendar not loaded yet -> getTimeArray returned [] regardless of the real window. Show the
    // loader rather than "no data", since the actual times/inferences appear once it loads.
    return 'loading';
  }
  if (times.length === 0) {
    return 'no-data'; // calendar loaded but the window is fully closed -- nothing to request
  }
  // the final point is the appended live "now" and churns on the per-minute refetch, so use the prior
  // (stable) point as the "load complete" marker
  const stableTimeMs = new Date(times[Math.max(0, times.length - 2)]).getTime();
  // inference arrays are built by mapping over ascending `times`, so the last element is newest
  const newestReceivedMs = Math.max(
    lastDateMs(bidInferences),
    lastDateMs(dealerInferences),
    lastDateMs(offerInferences),
  );
  if (newestReceivedMs < stableTimeMs) {
    return 'loading'; // still waiting for the recent data to arrive
  }
  // the recent end is in; now judge coverage
  const pointCount = Math.max(bidInferences.length, dealerInferences.length, offerInferences.length);
  if (pointCount < 2) {
    return 'no-data'; // fewer than 2 points can't form a line (single-point bond)
  }
  // require > 60% of the window populated, so switching to a longer period shows the loader (not a
  // sparse partial chart from the previous period's cache) until the new data arrives
  return pointCount > 0.6 * times.length ? 'ready' : 'loading';
}